import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { PREVIEW_MYTHS, TOTAL_MYTHS } from "@/lib/pmw-content";
import { Button } from "@/components/ui/button";
import { Lock, Check, Bell } from "lucide-react";


export const Route = createFileRoute("/prove-me-wrong")({
  head: () => ({
    meta: [
      { title: "Prove Me Wrong - 30 myths you've been repeating your whole life" },
      { name: "description", content: "30 things you've said out loud that were wrong. Each one debunked in 60 seconds, with the receipt. Launching soon - join the early access list." },
      { property: "og:title", content: "Prove Me Wrong - Smartasaurus" },
      { property: "og:description", content: "30 myths you've been repeating your whole life. Debunked. Receipts included." },
      { property: "og:url", content: "https://smartasaurus.com/prove-me-wrong" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Prove Me Wrong - Smartasaurus" },
      { name: "twitter:description", content: "30 myths you've been repeating your whole life. Debunked. Receipts included." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/prove-me-wrong" }],
  }),
  component: ProveMeWrongPage,
});

function ProveMeWrongPage() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content">
        {/* HERO */}
        <section className="border-b border-border bg-background">
          <div className="mx-auto max-w-4xl px-4 py-20 md:px-8 md:py-28">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Prove Me Wrong · Vol. 1 · Coming soon</div>
            <h1 className="mt-4 font-display text-5xl uppercase leading-[0.95] md:text-7xl">
              {TOTAL_MYTHS} things you've said out loud <span className="text-gradient-curiosity">that were wrong</span>.
            </h1>
            <p className="mt-6 max-w-2xl font-display text-xl uppercase leading-tight text-foreground md:text-2xl">
              Each one debunked in 60 seconds. With the receipt. So next time someone says it, you don't.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                size="lg"
                onClick={() => setWaitlistOpen(true)}
                className="text-base"
              >
                <Bell className="mr-2 h-4 w-4" /> Notify me when it launches
              </Button>
              <div className="text-sm text-muted-foreground">
                Launching soon · One email when it's ready · No spam
              </div>
            </div>

            <ul className="mt-10 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-primary" /> 30 myths most people still repeat</li>
              <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-primary" /> A one-line receipt for every claim</li>
              <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-primary" /> A one-line "ammo" line you can say at dinner</li>
              <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-primary" /> No filler, no theory, no class</li>
            </ul>
          </div>
        </section>

        {/* PREVIEW */}
        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-16 md:px-8 md:py-20">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Free preview</div>
            <h2 className="mt-3 font-display text-3xl uppercase leading-tight md:text-5xl">
              The first 10. Free.
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Read them. If even one of these surprised you, the other 20 will too.
            </p>

            <div className="mt-10 space-y-6">
              {PREVIEW_MYTHS.map((m) => (
                <MythCard key={m.id} myth={m} />
              ))}
            </div>
          </div>
        </section>

        {/* WAITLIST CTA */}
        <section id="waitlist-section" className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-16 md:px-8 md:py-20">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· The other 20</div>
            <h2 className="mt-3 font-display text-3xl uppercase leading-tight md:text-5xl">
              Get notified when it drops.
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              We're finishing the receipts and the design. Drop your email and you'll be first to know.
            </p>

            <div className="mt-10 grid gap-3">
              {["Napoleon was extremely short.", "Different parts of the tongue taste different things.", "Einstein failed math at school.", "Hair and nails keep growing after death.", "+ 16 more"].map((t, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground line-through">{t}</span>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Button size="lg" onClick={() => setWaitlistOpen(true)} className="text-base">
                <Bell className="mr-2 h-4 w-4" /> Notify me when it launches
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                One email. The moment it's ready. That's it.
              </p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      {waitlistOpen && (
        <WaitlistDialog productName="Prove Me Wrong - Vol. 1" onClose={() => setWaitlistOpen(false)} />
      )}
    </div>
  );
}


function MythCard({ myth }: { myth: { id: number; claim: string; truth: string; receipt: string; ammo: string } }) {
  return (
    <article className="rounded-xl border border-border bg-background p-5 md:p-6">
      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
        Myth #{String(myth.id).padStart(2, "0")}
      </div>
      <h3 className="mt-2 font-display text-xl uppercase leading-tight md:text-2xl">
        "{myth.claim}"
      </h3>
      <p className="mt-4 text-foreground"><span className="font-semibold text-primary">Truth: </span>{myth.truth}</p>
      <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold">Receipt: </span>{myth.receipt}</p>
      <p className="mt-2 text-sm text-foreground"><span className="font-semibold">Say this: </span>{myth.ammo}</p>
    </article>
  );
}
